import { useState, useEffect, useRef } from 'react';

const CHAT_WIDGET_ID = '69c288169788b123da81afa3';

type Platform = 'whatsapp' | 'instagram' | 'facebook' | 'tiktok' | 'sms' | 'chat';

interface ChatMessage { from: 'customer' | 'ai'; text: string; }

const conversations: Record<Platform, ChatMessage[]> = {
  whatsapp: [
    { from: 'customer', text: 'Available for boiler repair tomorrow?' },
    { from: 'ai',       text: 'Yes! 7 days a week 🔧 What\'s your address and preferred time?' },
    { from: 'customer', text: 'Clontarf, morning.' },
    { from: 'ai',       text: '9am or 11am? 📅' },
    { from: 'customer', text: '9am works' },
    { from: 'ai',       text: 'Booked ✅ Job ref #1042. See you at 9am!' },
  ],
  instagram: [
    { from: 'customer', text: 'Seen your ad — need heating help asap' },
    { from: 'ai',       text: 'On it! No heat, leak, or something else? 🏠' },
    { from: 'customer', text: 'No heat, kids at home' },
    { from: 'ai',       text: 'Urgent — address and best number? 🚨' },
    { from: 'customer', text: 'Rathmines, 087 123 4567' },
    { from: 'ai',       text: 'Got it ✅ Engineer on the way — ref #2089' },
  ],
  facebook: [
    { from: 'customer', text: 'Hi, do you cover Sandyford?' },
    { from: 'ai',       text: 'Yes! All of Dublin 🛠️ What do you need?' },
    { from: 'customer', text: 'Leaking radiator, pretty bad' },
    { from: 'ai',       text: 'Got it — address and a time that works?' },
    { from: 'customer', text: 'Sandyford, Wednesday afternoon' },
    { from: 'ai',       text: 'Wednesday 2pm booked ✅ Ref #3301' },
  ],
  tiktok: [
    { from: 'customer', text: 'Just saw your vid — is your AI real?!' },
    { from: 'ai',       text: 'Totally real 🤖🔧 Want a job booked right now?' },
    { from: 'customer', text: 'Haha yeah — need a plumber in Dundrum' },
    { from: 'ai',       text: 'On it! Best day this week for you? 📆' },
    { from: 'customer', text: 'Friday works' },
    { from: 'ai',       text: 'Friday AM locked in ✅ Job ref #4510!' },
  ],
  sms: [
    { from: 'customer', text: 'Hi, are you taking on new jobs?' },
    { from: 'ai',       text: 'Yes! 24/7 🔧 What\'s the job and location?' },
    { from: 'customer', text: 'Blocked drain, Blackrock' },
    { from: 'ai',       text: 'Got it. Tomorrow 10am suit you? ✅' },
    { from: 'customer', text: 'Perfect' },
    { from: 'ai',       text: 'Booked ✅ Ref #5612. See you at 10am!' },
  ],
  chat: [
    { from: 'customer', text: 'Hey, found you on Google. Need a quote.' },
    { from: 'ai',       text: 'Happy to help! What\'s the work and area? 🏡' },
    { from: 'customer', text: 'New bathroom install, Castleknock' },
    { from: 'ai',       text: 'Great choice! When suits for a site visit? 📅' },
    { from: 'customer', text: 'Saturday morning' },
    { from: 'ai',       text: 'Saturday 10am confirmed ✅ Ref #6701!' },
  ],
};

const platforms: { id: Platform; label: string; icon: string; color: string }[] = [
  { id: 'whatsapp',  label: 'WhatsApp',  icon: 'ri-whatsapp-line',   color: '#25D366' },
  { id: 'instagram', label: 'Instagram', icon: 'ri-instagram-line',  color: '#E1306C' },
  { id: 'facebook',  label: 'Facebook',  icon: 'ri-facebook-line',   color: '#1877F2' },
  { id: 'tiktok',    label: 'TikTok',    icon: 'ri-tiktok-line',     color: '#F5F5F5' },
  { id: 'sms',       label: 'SMS',       icon: 'ri-message-3-line',  color: '#AAFF45' },
  { id: 'chat',      label: 'Live Chat', icon: 'ri-chat-3-line',     color: '#AAFF45' },
];

type GHLWidget = HTMLElement & {
  open?: () => void;
  show?: () => void;
  openWidget?: () => void;
  shadowRoot: ShadowRoot | null;
};

function findChatWidget(): GHLWidget | null {
  const all = Array.from(document.querySelectorAll<GHLWidget>('chat-widget'));
  if (!all.length) return null;
  return (
    all.find((el) => el.getAttribute('widget-id') === CHAT_WIDGET_ID) ??
    all.find((el) => el.getAttribute('data-widget-id') === CHAT_WIDGET_ID) ??
    (all.length >= 2 ? all[all.length - 1] : all[0])
  );
}

export default function PlatformDemo() {
  const [active, setActive] = useState<Platform>('whatsapp');
  const [widgetReady, setWidgetReady] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const [tapHint, setTapHint] = useState('');
  const sectionRef = useRef<HTMLDivElement>(null);
  const chatRef    = useRef<HTMLDivElement>(null);
  const widgetRef  = useRef<GHLWidget | null>(null);

  const platform = platforms.find((p) => p.id === active)!;
  const messages = conversations[active];

  /* ── Reveal animations ── */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ── Scroll messages to bottom on platform switch ── */
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [active]);

  /* ── Detect GHL chat widget ── */
  useEffect(() => {
    let destroyed = false;
    let obs: MutationObserver | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;

    const startLoadingChatWidget = () => {
      if (destroyed) return;

      // Snapshot ALL widgets currently in DOM — voice widget should be here by now
      const preExisting = new Set(Array.from(document.querySelectorAll('chat-widget')));

      // Inject chat widget script only if not already present
      if (!document.querySelector('script[data-widget-id="69c288169788b123da81afa3"]')) {
        const script = document.createElement('script');
        script.src = 'https://beta.leadconnectorhq.com/loader.js';
        script.setAttribute('data-resources-url', 'https://beta.leadconnectorhq.com/chat-widget/loader.js');
        script.setAttribute('data-widget-id', '69c288169788b123da81afa3');
        document.body.appendChild(script);
      }

      const detect = () => {
        if (destroyed) return false;
        // Only pick up a widget that wasn't there before we injected — that's the chat widget
        const allWidgets = Array.from(document.querySelectorAll<GHLWidget>('chat-widget'));
        const chatWidget = allWidgets.find((el) => !preExisting.has(el as Element));
        if (chatWidget && !widgetRef.current) {
          widgetRef.current = chatWidget;
          setWidgetReady(true);
          return true;
        }
        return false;
      };

      if (detect()) return;

      obs = new MutationObserver(() => { detect(); });
      obs.observe(document.body, { childList: true, subtree: true });
      interval = setInterval(() => { if (detect() && interval) clearInterval(interval); }, 300);

      // Fallback — show ready state after 5s regardless
      setTimeout(() => { if (!destroyed) setWidgetReady(true); }, 5000);
    };

    // Wait for the voice widget to be confirmed in the DOM first,
    // then load the chat widget — this guarantees zero overlap
    const voiceReady = (window as Record<string, unknown>)['__ghlVoiceWidgetReady'] as Promise<HTMLElement> | undefined;
    if (voiceReady) {
      voiceReady.then(startLoadingChatWidget);
    } else {
      // VoiceDemo above hasn't set up the promise yet — wait a bit and try anyway
      setTimeout(startLoadingChatWidget, 2000);
    }

    return () => {
      destroyed = true;
      obs?.disconnect();
      if (interval) clearInterval(interval);
    };
  }, []);

  const handleOpenChat = () => {
    const el = widgetRef.current;
    if (!el) {
      setTapHint('Chat agent loading… tap again in a moment!');
      setTimeout(() => setTapHint(''), 3500);
      return;
    }
    setChatStarted(true);
    setTapHint('');

    // Unlock pointer-events on the widget host so the chat panel is tappable.
    // IMPORTANT: Only re-enable on interactive elements — NOT on generic divs/containers.
    // Using `* { pointer-events: auto }` would make large transparent overlay divs
    // inside the shadow DOM intercept ALL touch events, breaking page scroll on mobile.
    el.style.setProperty('pointer-events', 'auto', 'important');
    const shadow = el.shadowRoot;
    if (shadow) {
      let innerStyle = shadow.querySelector<HTMLStyleElement>('#ghl-mobile-inner');
      if (!innerStyle) {
        innerStyle = document.createElement('style');
        innerStyle.id = 'ghl-mobile-inner';
        shadow.prepend(innerStyle);
      }
      innerStyle.textContent = `
        :host { pointer-events: auto !important; }
        button,
        [role="button"],
        a,
        input,
        textarea,
        select,
        label,
        [contenteditable] {
          pointer-events: auto !important;
        }
        iframe {
          pointer-events: auto !important;
          max-height: calc(100svh - 120px) !important;
          border: none !important;
        }
        @media (max-width: 768px) {
          iframe { max-width: calc(100vw - 32px) !important; }
        }
      `;

      // ── Find and click the widget's OWN native launcher button ──
      // We do NOT call el.open() / el.show() — those APIs trigger the
      // booking flow. Instead we simulate a real user tap on the launcher
      // button so the widget opens in its default conversational mode.
      const nativeBtn =
        shadow.querySelector<HTMLElement>('[data-testid="chat-widget-launcher"]') ??
        shadow.querySelector<HTMLElement>('[class*="launcher"]') ??
        shadow.querySelector<HTMLElement>('[class*="chat-button"]') ??
        shadow.querySelector<HTMLElement>('[class*="open-button"]') ??
        shadow.querySelector<HTMLElement>('[class*="toggle"]') ??
        shadow.querySelector<HTMLElement>('button');

      if (nativeBtn) {
        nativeBtn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        return;
      }
    }

    // Last resort fallback
    el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-16 px-5 lg:px-8 overflow-hidden"
      style={{ backgroundColor: '#080F0C' }}
    >
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(10,92,68,0.5), transparent)' }} />

      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:gap-16">

          {/* ── Left: Text + Tabs ── */}
          <div className="md:flex-1 mb-12 md:mb-0">
            <div className="reveal opacity-0 mb-3" style={{ transitionDelay: '0ms' }}>
              <span className="font-dm font-semibold text-xs tracking-[0.2em] uppercase" style={{ color: '#AAFF45', textShadow: '0 0 10px rgba(170,255,69,0.5)' }}>
                ◆ SOLUTION 2 — YOUR AI ON EVERY CHANNEL
              </span>
            </div>

            <div className="reveal opacity-0 mb-3" style={{ transitionDelay: '80ms' }}>
              <h2 className="font-bebas text-brand-text" style={{ fontSize: 'clamp(38px, 5vw, 56px)' }}>
                One AI Agent.<br />Every Platform.<br />All Day. All Night.
              </h2>
            </div>

            <div className="reveal opacity-0 mb-7" style={{ transitionDelay: '160ms' }}>
              <p className="font-dm text-brand-grey text-base leading-relaxed">
                WhatsApp, Instagram, Facebook, TikTok, SMS or your website — customers message wherever they want. Your AI replies instantly, qualifies the lead, and books the job.
              </p>
            </div>

            {/* Platform tabs */}
            <div className="reveal opacity-0 mb-7" style={{ transitionDelay: '240ms' }}>
              <p className="font-dm text-xs uppercase tracking-widest mb-3" style={{ color: 'rgba(160,168,164,0.6)' }}>
                Tap a platform to see it in action
              </p>
              <div className="flex flex-wrap gap-2">
                {platforms.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setActive(p.id)}
                    className="whitespace-nowrap flex items-center gap-1.5 px-4 py-2.5 rounded-full font-dm text-xs font-medium cursor-pointer transition-all duration-200 min-h-[40px]"
                    style={{
                      backgroundColor: active === p.id ? 'rgba(10,92,68,0.5)' : 'rgba(255,255,255,0.05)',
                      color: active === p.id ? '#AAFF45' : '#A0A8A4',
                      border: active === p.id ? '1px solid rgba(170,255,69,0.4)' : '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <i className={`${p.icon} text-sm`} style={{ color: active === p.id ? '#AAFF45' : p.color }} />
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Caption + CTA hint */}
            <div className="reveal opacity-0 mb-7" style={{ transitionDelay: '320ms' }}>
              <p className="font-dm text-brand-grey text-sm leading-relaxed">
                This is what <strong style={{ color: '#F5F5F5' }}>your customers</strong> see —
                the AI handles it so you can focus on the job.
              </p>
            </div>

            {/* Stats */}
            <div className="reveal opacity-0 grid grid-cols-3 gap-3" style={{ transitionDelay: '400ms' }}>
              {[
                { value: '< 5s',  label: 'Response time' },
                { value: '24/7',  label: 'Always online' },
                { value: '3×',    label: 'More jobs booked' },
              ].map((s, i) => (
                <div key={i} className="rounded-xl p-3 text-center" style={{ backgroundColor: '#0a1812', border: '1px solid rgba(10,92,68,0.4)' }}>
                  <p className="font-bebas text-2xl" style={{ color: '#AAFF45' }}>{s.value}</p>
                  <p className="font-dm text-xs leading-tight" style={{ color: '#A0A8A4' }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: iPhone ── */}
          <div className="flex justify-center md:justify-end w-full md:w-auto">
            <div className="reveal opacity-0 w-full max-w-[296px]" style={{ transitionDelay: '200ms' }}>

              {/* Phone shell */}
              <div
                style={{
                  width: '100%',
                  borderRadius: '48px',
                  padding: '14px 12px 20px',
                  background: 'linear-gradient(160deg, #1c2e25 0%, #0d1f18 40%, #080F0C 100%)',
                  border: '2px solid rgba(10,92,68,0.8)',
                  boxShadow: '0 0 60px rgba(10,92,68,0.3), 0 0 100px rgba(10,92,68,0.1), inset 0 1px 0 rgba(255,255,255,0.06)',
                  position: 'relative',
                }}
              >
                {/* Side buttons */}
                <div style={{ position:'absolute', left:'-4px', top:'96px', width:'4px', height:'30px', borderRadius:'2px 0 0 2px', backgroundColor:'rgba(10,92,68,0.7)' }} />
                <div style={{ position:'absolute', left:'-4px', top:'138px', width:'4px', height:'52px', borderRadius:'2px 0 0 2px', backgroundColor:'rgba(10,92,68,0.7)' }} />
                <div style={{ position:'absolute', right:'-4px', top:'118px', width:'4px', height:'60px', borderRadius:'0 2px 2px 0', backgroundColor:'rgba(10,92,68,0.7)' }} />

                {/* Dynamic island */}
                <div className="flex justify-center mb-2.5">
                  <div style={{ width:'108px', height:'26px', borderRadius:'20px', backgroundColor:'#030806', boxShadow:'inset 0 0 8px rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                    <div style={{ width:'10px', height:'10px', borderRadius:'50%', backgroundColor:'#111a14' }} />
                    <div style={{ width:'6px', height:'6px', borderRadius:'50%', backgroundColor:'#0a1610' }} />
                  </div>
                </div>

                {/* Screen */}
                <div style={{ height:'500px', borderRadius:'28px', overflow:'hidden', display:'flex', flexDirection:'column', backgroundColor:'#080F0C', border:'1px solid rgba(10,92,68,0.25)' }}>

                  {/* Status bar */}
                  <div className="flex items-center justify-between px-4 pt-2 pb-1 flex-shrink-0" style={{ backgroundColor: '#0a1410' }}>
                    <span className="font-dm font-semibold text-brand-text" style={{ fontSize: '11px' }}>9:41</span>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-signal-wifi-fill text-brand-text" style={{ fontSize: '10px' }} />
                      <i className="ri-battery-fill text-brand-text" style={{ fontSize: '10px' }} />
                    </div>
                  </div>

                  {/* App header — changes with platform */}
                  <div className="flex items-center gap-2 px-3 py-2 flex-shrink-0" style={{ backgroundColor: '#0d1a14', borderBottom: '1px solid rgba(10,92,68,0.35)' }}>
                    <i className="ri-arrow-left-s-line" style={{ color: '#AAFF45', fontSize: '16px' }} />
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative transition-all duration-300"
                      style={{ backgroundColor: platform.color + '22', border: `1px solid ${platform.color}55` }}
                    >
                      <i className={`${platform.icon}`} style={{ color: platform.color, fontSize: '14px' }} />
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full animate-live-pulse" style={{ backgroundColor: '#AAFF45', border: '1.5px solid #0d1a14' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-dm font-bold text-brand-text leading-none" style={{ fontSize: '12px' }}>Murphy&apos;s Plumbing</p>
                      <p className="font-dm mt-0.5 flex items-center gap-1" style={{ color: '#AAFF45', fontSize: '9px' }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-accent animate-live-pulse inline-block" />
                        AI Agent Online · via {platform.label}
                      </p>
                    </div>
                    <i className="ri-video-line flex-shrink-0" style={{ color: 'rgba(170,255,69,0.4)', fontSize: '14px' }} />
                    <i className="ri-phone-line flex-shrink-0 ml-1" style={{ color: 'rgba(170,255,69,0.4)', fontSize: '14px' }} />
                  </div>

                  {/* Date pill */}
                  <div className="flex justify-center pt-2.5 pb-1 flex-shrink-0" style={{ backgroundColor: '#080F0C' }}>
                    <span className="font-dm px-3 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#606866', fontSize: '8px' }}>Today</span>
                  </div>

                  {/* Messages */}
                  <div ref={chatRef} className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-2" style={{ backgroundColor: '#080F0C' }}>
                    {messages.map((msg, i) => (
                      <div
                        key={`${active}-${i}`}
                        className={`flex items-end gap-1.5 ${msg.from === 'customer' ? 'justify-start' : 'justify-end'}`}
                        style={{ animation: 'platformFadeUp 0.3s ease both', animationDelay: `${i * 60}ms` }}
                      >
                        {msg.from === 'customer' && (
                          <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mb-0.5" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
                            <i className="ri-user-line" style={{ fontSize: '8px', color: '#A0A8A4' }} />
                          </div>
                        )}
                        <div
                          className="px-3 py-1.5 max-w-[78%]"
                          style={{
                            fontSize: '11px',
                            lineHeight: '1.45',
                            backgroundColor: msg.from === 'customer' ? 'rgba(255,255,255,0.09)' : 'rgba(10,92,68,0.65)',
                            color: msg.from === 'customer' ? '#D0D5D3' : '#F0F5F2',
                            borderRadius: msg.from === 'customer' ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
                          }}
                        >
                          {msg.text}
                          <div className="mt-0.5 text-right" style={{ fontSize: '8px', color: msg.from === 'customer' ? '#606866' : 'rgba(170,255,69,0.5)' }}>
                            {msg.from === 'ai' ? '✓✓ ' : ''}{['9:32','9:33','9:33','9:34','9:34','9:35'][i]}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Input bar / live CTA */}
                  <div
                    className="flex-shrink-0 relative"
                    style={{
                      backgroundColor: '#0a1410',
                      borderTop: '2px solid rgba(170,255,69,0.55)',
                      boxShadow: '0 -4px 20px rgba(170,255,69,0.1)',
                    }}
                  >
                    {/* Pulsing highlight bar at top of input area */}
                    <div
                      className="absolute top-0 left-0 right-0 h-px animate-pulse"
                      style={{ backgroundColor: 'rgba(170,255,69,0.7)' }}
                    />

                    <div className="flex items-center gap-2 px-3 py-2.5">
                      <i className="ri-add-circle-line flex-shrink-0" style={{ color: 'rgba(170,255,69,0.4)', fontSize: '18px' }} />
                      <button
                        type="button"
                        onClick={handleOpenChat}
                        className="flex-1 flex items-center rounded-full px-3 py-1.5 cursor-pointer transition-all duration-200 hover:brightness-125 animate-pulse"
                        style={{
                          backgroundColor: 'rgba(170,255,69,0.1)',
                          border: '1.5px solid rgba(170,255,69,0.6)',
                          boxShadow: '0 0 10px rgba(170,255,69,0.15)',
                        }}
                      >
                        <span className="font-dm font-semibold flex-1 text-left" style={{ fontSize: '11px', color: '#AAFF45', textShadow: '0 0 8px rgba(170,255,69,0.6)' }}>
                          Tap here to chat with the real AI…
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenChat}
                        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-all duration-200 active:scale-95 animate-bounce"
                        style={{ backgroundColor: '#AAFF45', boxShadow: '0 0 12px rgba(170,255,69,0.7)' }}
                      >
                        <i className="ri-send-plane-fill" style={{ fontSize: '14px', color: '#080F0C' }} />
                      </button>
                    </div>

                    {/* Live status */}
                    <div className="flex items-center justify-center gap-1.5 pb-2.5">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{
                          backgroundColor: widgetReady ? '#AAFF45' : '#A0A8A4',
                          boxShadow: widgetReady ? '0 0 5px rgba(170,255,69,0.9)' : 'none',
                          transition: 'all 0.4s ease',
                        }}
                      />
                      <p className="font-dm" style={{ fontSize: '9px', color: widgetReady ? 'rgba(170,255,69,0.7)' : '#606866' }}>
                        {tapHint || (chatStarted ? 'Chat opened — say hello!' : widgetReady ? 'LIVE · tap above to try the real AI' : 'Loading agent…')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Home indicator */}
                <div className="flex justify-center mt-3">
                  <div className="rounded-full" style={{ width: '100px', height: '4px', backgroundColor: 'rgba(255,255,255,0.18)' }} />
                </div>
              </div>

              {/* ── ATTENTION block — arrows pointing UP at the input bar ── */}
              <div className="flex flex-col items-center mt-3 mb-1">
                <div className="flex items-center gap-4 mb-1">
                  <i
                    className="ri-arrow-up-fill animate-bounce"
                    style={{ color: '#AAFF45', fontSize: '26px', filter: 'drop-shadow(0 0 6px rgba(170,255,69,0.8))' }}
                  />
                  <i
                    className="ri-cursor-fill animate-bounce"
                    style={{ color: '#AAFF45', fontSize: '22px', animationDelay: '0.15s', filter: 'drop-shadow(0 0 6px rgba(170,255,69,0.8))' }}
                  />
                  <i
                    className="ri-arrow-up-fill animate-bounce"
                    style={{ color: '#AAFF45', fontSize: '26px', animationDelay: '0.3s', filter: 'drop-shadow(0 0 6px rgba(170,255,69,0.8))' }}
                  />
                </div>
                <span
                  className="font-dm font-bold tracking-[0.18em] uppercase"
                  style={{ fontSize: '11px', color: '#AAFF45', textShadow: '0 0 10px rgba(170,255,69,0.7)' }}
                >
                  ↑ &nbsp; TAP THE BAR ABOVE TO CHAT &nbsp; ↑
                </span>
                <p className="font-dm text-xs mt-1" style={{ color: 'rgba(170,255,69,0.4)' }}>real ai · tap to try</p>
              </div>

            </div>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes platformFadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
